import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Plus, Pencil, Trash2, CopyPlus, Eye } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import FormBuilder from "@/components/FormBuilder";
import FormRenderer from "@/components/FormRenderer";
import { SAKRAMENT_LABELS } from "@/lib/constants";

const SAKS = ["TA", "EK", "FI", "EH"];
const uid = () => Math.random().toString(36).slice(2, 10);

export default function Anmeldeformulare() {
  const [items, setItems] = useState([]);
  const [dsTemplates, setDsTemplates] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const load = () => api.get("/form-templates").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/datenschutz-templates").then(({ data }) => setDsTemplates(data)).catch(() => {});
  }, []);

  const openNew = () => { setShowPreview(false); setEdit({ sakrament: "EK", name: "", fields: [] }); };
  const duplicate = (t) => { setShowPreview(false); setEdit({ sakrament: t.sakrament, name: `${t.name} (Kopie)`, fields: (t.fields || []) }); };
  const save = async () => {
    if (!edit.name.trim()) return toast.error("Bitte einen Namen vergeben.");
    try {
      const payload = { sakrament: edit.sakrament, name: edit.name, fields: edit.fields || [] };
      if (edit.id) await api.put(`/form-templates/${edit.id}`, payload);
      else await api.post("/form-templates", payload);
      toast.success("Gespeichert."); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Diese Formular-Vorlage wirklich löschen?")) return;
    await api.delete(`/form-templates/${id}`); toast.success("Gelöscht."); load();
  };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10" data-testid="form-templates-page">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3"><ClipboardList className="text-primary" /><h1 className="text-3xl font-serif">Anmeldeformulare</h1></div>
        <Button onClick={openNew} data-testid="ft-new-button"><Plus size={16} className="mr-1" /> Neue Vorlage</Button>
      </div>
      <p className="text-muted-foreground mb-6 text-sm">Standardisierte Anmeldeformular-Vorlagen je Sakrament. In jedem Kurs kann das Formular über „Als Vorlage speichern" hier abgelegt und über „Aus Vorlage" wieder übernommen werden.</p>

      <div className="space-y-8">
        {SAKS.map((s) => {
          const list = items.filter((t) => t.sakrament === s);
          return (
            <div key={s} data-testid={`ft-group-${s}`}>
              <h2 className="text-lg font-semibold mb-3">{SAKRAMENT_LABELS[s]}</h2>
              {list.length === 0 ? (
                <p className="text-sm text-muted-foreground">Noch keine Vorlage.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {list.map((t) => (
                    <div key={t.id} className="bg-white border border-border rounded-lg shadow-sm p-4 flex items-center justify-between" data-testid={`ft-item-${t.id}`}>
                      <div>
                        <p className="font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{(t.fields || []).filter((f) => !["heading", "pagebreak"].includes(f.type)).length} Felder</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setShowPreview(false); setEdit({ id: t.id, sakrament: t.sakrament, name: t.name, fields: t.fields || [] }); }} data-testid={`ft-edit-${t.id}`}><Pencil size={15} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => duplicate(t)} title="Duplizieren" data-testid={`ft-duplicate-${t.id}`}><CopyPlus size={15} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => del(t.id)} data-testid={`ft-delete-${t.id}`}><Trash2 size={15} className="text-destructive" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Formular-Vorlage bearbeiten" : "Neue Formular-Vorlage"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4 py-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Sakrament</Label>
                  <Select value={edit.sakrament} onValueChange={(v) => setEdit({ ...edit, sakrament: v })}>
                    <SelectTrigger data-testid="ft-edit-sakrament"><SelectValue /></SelectTrigger>
                    <SelectContent>{SAKS.map((s) => <SelectItem key={s} value={s}>{SAKRAMENT_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Name der Vorlage</Label>
                  <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="z.B. Firmung Standard" data-testid="ft-edit-name" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Formularfelder</Label>
                <FormBuilder fields={edit.fields || []} onChange={(f) => setEdit({ ...edit, fields: f })} dsTemplates={dsTemplates} sakrament={edit.sakrament} />
              </div>
              <div className="space-y-2 border-t border-border pt-3">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)} data-testid="ft-preview-toggle"><Eye size={15} className="mr-1" /> {showPreview ? "Vorschau ausblenden" : "Vorschau anzeigen"}</Button>
                {showPreview && (
                  <div className="border border-border rounded-lg p-4 bg-slate-50" data-testid="ft-preview">
                    <FormRenderer courseId="preview" skipRequired submitting={false} submitLabel="Nur Vorschau" onSubmit={() => {}}
                      fields={edit.fields || []} dsTemplates={dsTemplates}
                      consents={[{ key: "psb1", label: "Personensorgeberechtigte 1" }, { key: "psb2", label: "Personensorgeberechtigte 2" }]} />
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="ft-save-button">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
